import { group, type InternMap } from 'd3';
import { groups } from 'd3-array';
import { get, isArray, isNil, uniqBy } from 'lodash';

import type {
  SelectionList,
  SelectionListIdNames,
  TreeItem,
  ValidValueTypes,
} from '@revelio/iso-utility';

import type { NormalizedSelectionListItem, SelectionListItems } from '../types';

// ---- TreeData type (mirrors arborist tree node shape) ----

export type TreeData = {
  id: string;
  name: string;
  depth: number;
  ticker?: string;
  shortName?: string;
  industry?: unknown;
  rics_k50?: unknown;
  rics_k400?: unknown;
  nodeCount?: number;
  linkedIds?: string[];
  suggestedTitles?: string[];
  children?: TreeData[];
};

export type { TreeItem };

// ---- normalizeSelectionLists ----

export const normalizeSelectionLists = (
  lists: SelectionList<ValidValueTypes>[]
): SelectionListItems[] =>
  lists.map(
    (list): SelectionListItems => ({
      id: list.id as SelectionListIdNames,
      value: list.value.map(
        (item): NormalizedSelectionListItem => ({
          ...item,
          id: `${item.id}`,
        })
      ),
    })
  );

// ---- getSelectionListItem ----

type GetSelectionListItemProps = {
  id: string;
  selectionLists: SelectionListItems[];
};

export const getSelectionListItem = ({
  id,
  selectionLists,
}: GetSelectionListItemProps):
  | (NormalizedSelectionListItem & { selectionListId: string })
  | null => {
  const splitIds = id.split('_');
  const splitIdsLength = splitIds.length;
  const lastSegment = splitIds[splitIdsLength - 1];
  const lastId = lastSegment?.includes(':')
    ? lastSegment.split(':').pop() || ''
    : lastSegment;

  if (!lastId) return null;

  const selectionList = selectionLists[splitIdsLength - 1];
  const selectedItem = selectionList?.value.find((item) => item.id === lastId);

  if (!selectedItem || !selectionList) return null;

  return { ...selectedItem, selectionListId: selectionList.id };
};

// ---- convertSelectionToTreeItems ----

type ConvertSelectionToTreeItems = {
  selections: string[];
  selectionLists: SelectionListItems[];
};

export const convertSelectionToTreeItems = ({
  selections,
  selectionLists,
}: ConvertSelectionToTreeItems): Record<string, TreeItem> => {
  const normalizedSelections = selections.map((id) => {
    const parts = id.split('_');
    const maxParts = Math.min(parts.length, selectionLists.length);
    return parts.slice(parts.length - maxParts).join('_');
  });

  const selectedItems = normalizedSelections
    .map((selectionId) =>
      getSelectionListItem({ id: selectionId, selectionLists })
    )
    .filter(
      (
        item
      ): item is NormalizedSelectionListItem & { selectionListId: string } =>
        item !== null
    );

  const treeItems: TreeItem[] = selectedItems.map((item) => ({
    id: `${item.selectionListId}.${item.id}`,
    selectionListId: item.selectionListId as SelectionListIdNames,
    item: {
      id: item.id,
      shortName: item.shortName,
      longName: item.longName,
      label: item.longName,
    },
    children: [],
  }));

  const filters = groups(treeItems, (d) => d.id);
  return filters.reduce<Record<string, TreeItem>>((acc, [key, values]) => {
    const value = values[0];
    if (!value) return acc;
    return { ...acc, [key]: value };
  }, {});
};

// ---- nestSelectionLists ----

type LinkedNormalizedSelectionListItem = NormalizedSelectionListItem & {
  linkedParentIds?: string[];
  selectionListId?: string;
};

type NestRecursiveProps = {
  selectionListItems: NormalizedSelectionListItem[];
  childMaps: InternMap<
    number | string | string[] | undefined,
    NormalizedSelectionListItem[]
  >[];
  nestedParentId?: string;
  depth: number;
  headcount?: number;
};

const at = <T>(arr: T[] | undefined, index: number): T | undefined =>
  Array.isArray(arr) ? arr[index] : undefined;

const nestRecursive = ({
  selectionListItems,
  childMaps,
  nestedParentId,
  depth,
  headcount = 0,
}: NestRecursiveProps): TreeData[] =>
  selectionListItems.map(
    (item: LinkedNormalizedSelectionListItem): TreeData => {
      const segment = `${depth}:${item.id}`;
      const nestedId = nestedParentId
        ? `${nestedParentId}_${segment}`
        : segment;
      const firstMap = at(childMaps, 0);
      const children = firstMap && firstMap.get(item.id);

      return {
        id: nestedId,
        name: (item.longName as string) || item.id,
        depth,
        ...(typeof item.ticker === 'string' && { ticker: item.ticker }),
        ...(typeof item.shortName === 'string' && {
          shortName: item.shortName,
        }),
        ...(item.rics_k10 != null && { industry: item.rics_k10 }),
        ...(item.rics_k50 != null && { rics_k50: item.rics_k50 }),
        ...(item.rics_k400 != null && { rics_k400: item.rics_k400 }),
        ...(item.headcount != null && { nodeCount: Number(item.headcount) }),
        ...(item?.linkedParentIds
          ? {
              linkedIds: item?.linkedParentIds
                ?.filter((id: string) => {
                  const lastSeg = nestedParentId?.split('_').at(-1);
                  const parentRawId =
                    lastSeg && lastSeg.includes(':')
                      ? lastSeg.split(':').pop()
                      : lastSeg;
                  return id !== parentRawId;
                })
                .map(
                  (id: string) =>
                    `${nestedParentId?.split('_').slice(0, -1).join('_')}_${id}_${item.id}`
                ),
            }
          : {}),
        ...(typeof item.topCleanedTitles === 'string' && {
          suggestedTitles: item.topCleanedTitles
            .split(',')
            .map((title: string) => title.trim()),
        }),
        ...(Array.isArray(item.rawTitles) && {
          suggestedTitles: item.rawTitles as string[],
        }),
        ...(typeof item.topSkills === 'string' && {
          suggestedTitles: item.topSkills
            .split(',')
            .map((skill: string) => skill.trim()),
        }),
        ...(children && {
          children: nestRecursive({
            selectionListItems: uniqBy(children, (c) => c.id),
            childMaps: childMaps.slice(1),
            nestedParentId: nestedId,
            depth: depth + 1,
            headcount:
              item.headcount != null ? Number(item.headcount) : headcount,
          }),
        }),
      };
    }
  );

export const nestSelectionLists = (
  selectionLists: SelectionListItems[],
  nameKey: keyof NormalizedSelectionListItem = 'longName'
): TreeData[] => {
  if (selectionLists.length === 0) return [];
  if (selectionLists.length === 1) {
    const first = at(selectionLists, 0);
    const firstValues = uniqBy(first?.value ?? [], (i) => i.id);
    return firstValues.map((item) => ({
      id: `0:${item.id}`,
      name: get(item, nameKey, item.id) as string,
      depth: 0,
      ...(!isNil(item.headcount) && {
        nodeCount: Number(item.headcount),
      }),
    }));
  }

  const groupedLists = selectionLists.slice(1).map((list, i) => {
    const parentListName = (at(selectionLists, i)?.id ||
      '') as keyof NormalizedSelectionListItem;

    const listToGroup = uniqBy(list.value, (it) => it.id).flatMap((item) => {
      const parentIds = item[parentListName];
      const parentIdArrayRaw = isArray(parentIds) ? parentIds : [parentIds];
      const parentIdArray = Array.from(
        new Set(parentIdArrayRaw.filter((p) => p != null))
      );

      return parentIdArray.map((parentId) => ({
        ...item,
        [parentListName]: parentId,
        ...(parentIdArray.length > 1 ? { linkedParentIds: parentIdArray } : {}),
      }));
    });

    return group(
      listToGroup,
      (d) =>
        (d as Record<string, unknown>)[parentListName] as
          | string
          | number
          | undefined
    );
  });

  const first = at(selectionLists, 0);
  const firstValues = uniqBy(first?.value ?? [], (i) => i.id);

  return nestRecursive({
    selectionListItems: firstValues,
    childMaps: groupedLists,
    depth: 0,
  });
};

// ---- getNestedId ----

export type GetNestedIdProps = {
  selectionLists: SelectionListItems[];
  item: { id: string; selectionListId: string };
  nestedChildId?: string;
};

export const getNestedId = ({
  selectionLists,
  item,
  nestedChildId,
}: GetNestedIdProps): string => {
  const level = selectionLists.findIndex(
    (list) => list.id === item?.selectionListId
  );
  const currentSegment = `${level}:${item.id}`;
  if (level <= 0) return nestedChildId || currentSegment;

  const selectionList = at(selectionLists, level);
  const selectionListItem = selectionList?.value.find(
    (value) => value.id === item.id
  );

  if (!selectionListItem) return `${item.id}`;

  const parentSelectionList = at(selectionLists, level - 1);
  const parentSelectionListId = (parentSelectionList?.id ||
    '') as keyof NormalizedSelectionListItem;
  const parentId = selectionListItem[parentSelectionListId];

  const parentItem = parentSelectionList?.value.find((it) => {
    if (isArray(parentId)) return parentId.includes(it.id);
    return it.id === parentId;
  });

  if (!parentId || !parentItem) return nestedChildId || currentSegment;
  const parentSegment = `${level - 1}:${parentItem.id}`;
  const nestedId = (() => {
    if (nestedChildId) return `${parentSegment}_${nestedChildId}`;
    else return `${parentSegment}_${currentSegment}`;
  })();

  return getNestedId({
    selectionLists: selectionLists.slice(0, level),
    item: {
      selectionListId: parentSelectionListId as string,
      id: parentItem.id,
    },
    nestedChildId: nestedId,
  });
};
